/** @internal – import from `@/store/useDepartmentStore` instead. */
import { RESEARCH_PIPELINE_STEPS } from "@/config/coAgentPrompts";
import { SOCIAL_PIPELINE_STEPS } from "@/config/socialCoAgentPrompts";
import { BRAND_PIPELINE_STEPS } from "@/config/brandCoAgentPrompts";
import type { BrandCoAgentInput } from "@/config/brandCoAgentPrompts";
import type {
  GrowthPipelineInput,
  GrowthTrackingEvent,
  GrowthCampaign,
} from "@/config/growthCoAgentPrompts";
import type {
  GeoPipelineInput,
  AiCitationResult,
  GeoScore,
  ContentGap,
  CopyUpgrade,
  JsonLdAsset,
  EntityPayload,
  SiteScanResult,
  GeoImplementationPack,
} from "@/config/geoCoAgentPrompts";
import { ECOMMERCE_PIPELINE_STEPS } from "@/config/ecommerceCoAgentPrompts";
import type {
  EcommercePipelineInput,
  BrandProfile as EcommerceBrandProfile,
} from "@/config/ecommerceCoAgentPrompts";
import { GROWTH_PIPELINE_STEPS } from "@/config/growthCoAgentPrompts";
import { GEO_PIPELINE_STEPS } from "@/config/geoCoAgentPrompts";
import type {
  BrandInput,
  GeneratedQueryPlan,
  CollectionReport,
  GeoVisibilityMatrix,
  GeoPlan,
  AdsPlan,
  ContentPlan,
} from "@/config/coAgentPrompts";
import type {
  SocialBrandInput,
  SocialQueryPlan,
  TrendReport,
  CompetitorAudit,
  TemplateLibrary,
  SocialCalendar,
  VideoBriefBatch,
  EngagementBenchmark,
} from "@/config/socialCoAgentPrompts";
import type {
  BrandPopulateResult,
  BrandConfig,
  TwitterResearchResult,
  PerplexityResearchResult,
  YoutubeResearchResult,
  ResearchSynthesis,
} from "@/config/brand";
import type { WidgetContext } from "../../components/context-inspector/types";

export type { WidgetContext };

/** Inlined from core `domains/brand/generators/promptEngine`. */
export interface BrandResearchQueryPlan {
  [key: string]: unknown;
  xai_x_queries: string[];
  perplexity_queries: string[];
  youtube_queries: string[];
  google_search_queries: string[];
}

export type CoAgentWidgetType =
  | "ads"
  | "researchProgress"
  | "geoPlan"
  | "adsPlan"
  | "contentPlan"
  | "socialProgress"
  | "trendReport"
  | "competitorAudit"
  | "templateLibrary"
  | "socialCalendar"
  | "videoBrief"
  | "videoProductionQueue"
  // Brand coagent widgets
  | "brandInput"
  | "brandResearchProgress"
  | "brandStrategy"
  | "brandProfile"
  | "brandLogoReview"
  | "brandIconReview"
  | "brandVisualReview"
  | "brandCharacterReview"
  | "brandSave"
  | "brandDeck"
  // Growth coagent widgets
  | "growthProgress"
  | "trackingPlan"
  | "campaignReview"
  | "googleAuth"
  // GEO coagent widgets
  | "geoProgress"
  | "geoScorecard"
  | "geoAssetReview"
  | "geoEntityChecklist"
  | "geoImplementation"
  // E-commerce coagent widgets
  | "shopifyAuth"
  | "storeConnect"
  | "ecommerceProgress"
  | "productCatalog"
  | "adEstimates"
  | "strategyDashboard"
  // E-commerce ad cost widgets
  | "adCostComparison"
  // E-commerce ads setup widgets
  | "adPlatformConnect"
  | "adsOptimization"
  // Meta Ad Platform widgets (Phase 2)
  | "metaConnection"
  | "campaignList"
  | "campaignDashboard"
  | "creativeLibrary"
  // Content Intelligence widgets (Phase 3)
  | "contentIntelligence";

// Phase 15-12 — Saga lives under marketing/social on the unified
// agent runtime. Adding 'social' here mirrors the same addition on
// `frontend/src/config/departments.ts::CoAgentType` so the team
// config's coAgentType field validates.
export type CoAgentType = "geo" | "ads" | "content" | "forge" | "products" | "brand-dna" | "social";

export type CoAgentMessageSource =
  | "user"
  | "sse"
  | "companion"
  | "tool_dispatch"
  | "pipeline"
  | "system";

export type CoAgentMessage = {
  id: string;
  role: "user" | "agent" | "system";
  content?: string;
  widget?: CoAgentWidgetType;
  /** Message producer. Used for ownership checks + diagnostics. */
  source?: CoAgentMessageSource;
  /** Optional stable key used to dedupe semantically-identical companion messages. */
  dedupeKey?: string;
  timestamp: Date;
};

export type StepStatus = "pending" | "running" | "waiting" | "completed" | "error" | "failed";

export const PIPELINE_STEPS = RESEARCH_PIPELINE_STEPS.map((s) => s.label);
export const SOCIAL_STEPS = SOCIAL_PIPELINE_STEPS.map((s) => s.label);
export const BRAND_STEPS = BRAND_PIPELINE_STEPS.map((s) => s.label);
export const GROWTH_STEPS = GROWTH_PIPELINE_STEPS.map((s) => s.label);
export const GEO_STEPS = GEO_PIPELINE_STEPS.map((s) => s.label);
export const ECOMMERCE_STEPS = ECOMMERCE_PIPELINE_STEPS.map((s) => s.label);

export interface GeoInsights {
  summary: string;
  demographics: string;
  industries: string;
  adLandscape: string;
  opportunities: string;
}

export interface CompetitorData {
  name: string;
  mentionCount?: number;
  angle: string;
}

export interface ValuePropData {
  id: string;
  title: string;
  description: string;
  reasoning: string;
  icon: string;
}

export interface AutomationStep {
  type: "delay" | "email" | "sms" | "webhook";
  delay?: string;
  content?: string;
  subject?: string;
}

export interface CampaignData {
  geo?: string;
  geoInsights?: GeoInsights;
  competitors?: CompetitorData[];
  valueProps?: ValuePropData[];
  selectedValueProp?: string;
  selectedValuePropTitle?: string;
  script?: string;
  estimatedDuration?: string;
  videoAssetUrl?: string;
  videoCreditEstimate?: number;
  automationSteps?: AutomationStep[];
  budget?: number;
  totalCreditsUsed?: number;
  // Research engine data
  brandInput?: BrandInput;
  queryPlan?: GeneratedQueryPlan;
  researchStatus?: "idle" | "running" | "complete" | "error";
  researchPhase?:
    | "prompt_generation"
    | "data_collection"
    | "distillation"
    | "synthesis"
    | "done"
    | "error";
  researchProgress?: { phase: string; completed: number; total: number };
  collectionReport?: Partial<CollectionReport>;
  geoMatrix?: GeoVisibilityMatrix;
  geoPlan?: GeoPlan;
  adsPlan?: AdsPlan;
  contentPlan?: ContentPlan;
  // Social coagent data
  socialBrandInput?: SocialBrandInput;
  socialQueryPlan?: SocialQueryPlan;
  socialResearchStatus?: "idle" | "running" | "complete" | "error";
  socialResearchPhase?: string;
  socialResearchProgress?: { phase: string; completed: number; total: number };
  trendReport?: TrendReport;
  competitorAudit?: CompetitorAudit;
  templateLibrary?: TemplateLibrary;
  socialCalendar?: SocialCalendar;
  videoBriefs?: VideoBriefBatch;
  engagementBenchmarks?: Record<string, EngagementBenchmark>;
  videosCollected?: number;
  transcriptsExtracted?: number;
  // Social video generation
  socialVideoTemplates?: Array<{ id: string; name: string; description: string; tags?: string[] }>;
  socialVideoPreviews?: Record<string, string>;
  socialGeneratedVideos?: Array<{
    id: string;
    source: "template" | "custom" | "brief";
    templateId?: string;
    briefId?: string;
    title: string;
    aspectRatio: "16:9" | "9:16";
    prompt: string;
    assetUrl: string;
    createdAt: string;
  }>;
  socialVideoGenerationState?: {
    status: "idle" | "running" | "complete" | "error";
    phase?: string;
    progressMessage?: string;
    error?: string;
  };
  socialProductionHandoffs?: Array<{
    id: string;
    briefId: string;
    pipeline: "cinematic" | "ugc";
    sessionId: string;
    sessionSlug: string;
    workflowSlug: "video-cinematic" | "video-ugc";
    createdAt: string;
  }>;
  // Brand coagent data
  brandCoAgentInput?: BrandCoAgentInput;
  brandWebsiteContent?: string;
  brandQueryPlan?: BrandResearchQueryPlan;
  brandResearchStatus?: "idle" | "running" | "complete" | "error";
  brandResearchPhase?: string;
  /**
   * Tracks whether the active brand-research run is part of the AI-create
   * wizard (`create`) or a refresh dispatched from `/brands/:id`
   * (`refresh`). The brand SSE subscriber consults this to decide
   * whether to mutate wizard step state / chat / focus.
   */
  brandResearchMode?: "create" | "refresh";
  /**
   * When set, the current brand-research run will persist its
   * synthesis back onto the matching brand artifact rather than
   * spinning up a fresh one.
   */
  brandResearchTargetBrandId?: string;
  brandResearchProgress?: { phase: string; completed: number; total: number };
  brandCollectionTrackProgress?: Record<string, { completed: number; total: number }>;
  brandTwitterResult?: TwitterResearchResult;
  brandPerplexityResult?: PerplexityResearchResult;
  brandYoutubeResult?: YoutubeResearchResult;
  brandGoogleSearchResult?: PerplexityResearchResult;
  brandSynthesis?: ResearchSynthesis;
  brandStrategyAwaitingConfirmation?: boolean;
  brandPendingPopulateResult?: BrandPopulateResult;
  brandPopulateResult?: BrandPopulateResult;
  brandConfig?: BrandConfig;
  brandSavedId?: string;
  brandLogoOptions?: Array<{
    imageData: string;
    mimeType?: string;
    aesthetic: string;
    label: string;
    promptUsed: string;
    /** @deprecated Use imageData instead */
    svgData?: string;
  }>;
  brandLogoSelectedIndex?: number;
  brandLogoGenerationStatus?: "idle" | "generating" | "complete" | "error";
  brandIconManifest?: Array<{
    name: string;
    description: string;
    category?: "core" | "product" | "commerce" | "content" | "support" | "growth" | "operations";
  }>;
  brandIconManifestStatus?: "idle" | "generating" | "complete" | "error";
  brandIconResults?: Array<{
    name: string;
    svgString: string;
    pngBase64: string;
    imageUrl?: string;
    url?: string;
  }>;
  brandIconGenerationStatus?: "idle" | "generating" | "complete" | "error";
  brandAccessoryAssets?: {
    iconMoodboard?: Array<{
      imageData: string;
      mimeType?: string;
      imageUrl?: string;
      url?: string;
      aesthetic: "iconPack";
      label: string;
      variantIndex: 1 | 2;
      variantName: "A" | "B";
      promptUsed: string;
      svgData?: string;
    }>;
    swagSticker?: Array<{
      imageData: string;
      mimeType?: string;
      imageUrl?: string;
      url?: string;
      aesthetic: "swagSticker";
      label: string;
      variantIndex: 1 | 2;
      variantName: "A" | "B";
      promptUsed: string;
      svgData?: string;
    }>;
  };
  brandAccessoryGenerationStatus?: "idle" | "generating" | "complete" | "error";
  brandBackgroundAssets?: Array<{
    imageData: string;
    mimeType?: string;
    imageUrl?: string;
    url?: string;
    target: "twitterBanner" | "linkedinBanner" | "websiteHero";
    label: string;
    aspectRatio: "3:1" | "4:1" | "16:9";
    promptUsed: string;
    variantIndex: 1;
    variantName: "A";
    svgData?: string;
  }>;
  brandBackgroundGenerationStatus?: "idle" | "generating" | "complete" | "error";
  // GEO pipeline data
  geoInput?: GeoPipelineInput;
  geoResearchStatus?: "idle" | "running" | "complete" | "error";
  geoResearchPhase?: string;
  geoResearchProgress?: { phase: string; completed: number; total: number };
  siteScan?: SiteScanResult;
  aiCitations?: AiCitationResult[];
  aiShareOfVoice?: number;
  geoScoreResult?: GeoScore;
  contentGaps?: ContentGap[];
  geoTopCompetitors?: string[];
  jsonLdAssets?: JsonLdAsset[];
  copyUpgrades?: CopyUpgrade[];
  entityPayload?: EntityPayload;
  geoImplementationPack?: GeoImplementationPack;
  // Growth pipeline data
  growthInput?: GrowthPipelineInput;
  growthResearchStatus?: "idle" | "running" | "complete" | "error";
  growthResearchPhase?: string;
  growthResearchProgress?: { phase: string; completed: number; total: number };
  websiteAnalysis?: {
    url: string;
    title: string;
    summary: string;
    funnelType: string;
    pages: string[];
  };
  trackingEvents?: GrowthTrackingEvent[];
  googleIds?: { gtmContainerId?: string; ga4PropertyId?: string; gadsCustomerId?: string };
  googleAuthStatus?: "pending" | "authenticated" | "accounts_selected";
  provisioningStatus?: { gtm: string; ga4: string; linking: string };
  growthCampaigns?: GrowthCampaign[];
  gtmSnippet?: string;
  deploymentStatus?: "pending" | "deployed" | "error";
  recorderAnalysis?: Record<string, unknown>;
  recorderSkipped?: boolean;
  // E-commerce pipeline data
  ecommerceInput?: EcommercePipelineInput;
  ecommerceResearchStatus?: "idle" | "running" | "complete" | "error";
  ecommerceResearchPhase?: string;
  ecommerceResearchProgress?: { phase: string; completed: number; total: number };
  ecommerceLastErrorMessage?: string;
  ecommerceMissingDataSummary?: {
    missingCogs: number;
    missingCac: number;
  };
  ecommerceSubPhases?: Array<{
    phase: string;
    label: string;
    status: "pending" | "running" | "complete" | "error";
  }>;
  // File extraction (manual upload)
  ecommerceFileExtractionStatus?:
    | "detecting_schema"
    | "extracting_products"
    | "preview"
    | "complete"
    | "error";
  ecommerceExtractionResult?: Record<string, unknown> | null;
  shopInfo?: { shopDomain: string; shopName: string; shopCurrency: string };
  ecommerceStoreId?: string;
  ecommerceBrandProfile?: EcommerceBrandProfile;
  ecommerceProducts?: Array<{
    title: string;
    handle: string;
    status: string;
    productType?: string;
    vendor?: string;
    priorityScore?: number;
    recommendation?: string;
    category?: string;
    adAngle?: string;
    images?: Array<{ url: string }>;
    variants?: Array<{ price: string; inventoryQuantity: number }>;
  }>;
  ecommerceProductCount?: number;
  ecommerceCatalogSummary?: {
    totalProducts: number;
    activeProducts: number;
    priceRange: { min: number; max: number } | null;
    cogsCount: number;
    cogsCoverage: number;
    topTags: string[];
  };
  ecommerceCategories?: Array<{
    productIndex: number;
    category: string;
    subcategory?: string;
    adAngle: string;
    targetKeywords: string[];
  }>;
  ecommerceMetrics?: Record<string, unknown>;
  ecommerceScores?: Array<{
    productIndex: number;
    overallScore: number;
    dimensions: Record<string, number>;
    recommendation: string;
    reasoning: string;
  }>;
  ecommerceProductMetrics?: import("@/features/products").ProductMetric[];
  // Ad cost estimation & synthesis
  ecommerceAdCosts?: Array<{
    productId: string;
    platforms: Record<
      string,
      {
        estimatedCpc: number;
        estimatedCpm: number;
        estimatedCpv: number;
        estimatedCpa: number;
        estimatedCtr: number;
        estimatedReach: number;
        searchVolume: number;
        competitionLevel: string | null;
        projectedCpa: number;
        conversionRateUsed: number;
        dataSource: "api" | "benchmark" | "hybrid";
        confidence: "high" | "medium" | "low" | "none";
      }
    >;
  }>;
  ecommercePortfolioSynthesis?: {
    rankedProducts: Array<{
      productId: string;
      title: string;
      weightedCpa: number;
      estimatedLtvCac: number | null;
      cpaHeadroom: number;
      shouldAdvertise: boolean;
      bestPlatform: string | null;
      bestPlatformCpa: number;
      platformCosts: Record<string, { cpa: number; confidence: string }>;
      projections: Array<{
        monthlyBudget: number;
        estimatedConversions: number;
        estimatedRevenue: number;
        estimatedProfit: number;
        estimatedRoas: number;
      }>;
    }>;
    budgetScenarios: Array<{
      revenueTarget: number;
      requiredBudget: number;
      productMix: Array<{
        productId: string;
        title: string;
        allocation: number;
        expectedRevenue: number;
      }>;
      channelMix: Record<string, number>;
      feasible: boolean;
      feasibilityNote: string;
    }>;
    summary: {
      totalProducts: number;
      adReadyProducts: number;
      avgWeightedCpa: number;
      avgRoas: number;
      requiredBudgetFor10k: number;
      portfolioConfidence: "high" | "medium" | "low";
    };
  };
  ecommerceGeminiInsights?: {
    portfolioVerdict?: string;
    budgetRecommendation?: string;
    topRecommendations?: string[];
    risks?: string[];
    opportunities?: string[];
    channelStrategy?: string;
  };
  // Meta Ad Platform (Phase 2)
  metaConnected?: boolean;
  metaAccountId?: string;
  metaAccountName?: string;
  metaCurrency?: string;
  metaCampaigns?: Array<{
    id: string;
    name: string;
    objective: string;
    status: string;
    dailyBudget: number;
    spend: number;
    impressions: number;
    clicks: number;
    conversions: number;
    roas: number;
    ctr: number;
    cpc: number;
  }>;
  metaCreatives?: Array<{
    id: string;
    productId: string;
    headline: string;
    description: string;
    cta: string;
    imageUrls: string[];
    format: string;
    status: string;
    performanceScore: number | null;
  }>;
  metaCampaignMetrics?: Array<{
    date: string;
    spend: number;
    impressions: number;
    clicks: number;
    conversions: number;
    conversionValue: number;
    roas: number;
    ctr: number;
    cpc: number;
    cpm: number;
  }>;
  // TikTok Ad Platform
  tiktokConnected?: boolean;
  tiktokAccountId?: string;
  tiktokAccountName?: string;
  tiktokAdvertiserId?: string;
  tiktokCurrency?: string;
  tiktokCampaigns?: Array<{
    id: string;
    name: string;
    objective: string;
    status: string;
    dailyBudget: number;
    spend: number;
    impressions: number;
    clicks: number;
    conversions: number;
    roas: number;
    ctr: number;
    cpc: number;
  }>;
  tiktokCreatives?: Array<{
    id: string;
    type: "video" | "image";
    fileName: string;
    width: number;
    height: number;
    duration?: number;
    status: string;
  }>;
  tiktokCampaignMetrics?: Array<{
    date: string;
    spend: number;
    impressions: number;
    clicks: number;
    conversions: number;
    costPerConversion: number;
    roas: number;
    ctr: number;
    cpc: number;
    cpm: number;
  }>;
  tiktokTrends?: {
    hashtags: Array<{ id: string; name: string; videoCount: number; viewCount: number }>;
    music: Array<{ id: string; title: string; artist: string; usageCount: number }>;
  };
  tiktokAudiences?: Array<{
    id: string;
    name: string;
    type: string;
    size: number;
  }>;
  tiktokCatalogId?: string;
  // Content Intelligence (Phase 3)
  contentIntelligenceStatus?: "idle" | "running" | "complete" | "error";
  contentIntelligencePhase?: string;
  contentIntelligenceSubPhases?: Array<{
    phase: string;
    label: string;
    status: "pending" | "running" | "complete" | "error";
  }>;
  metaTopAds?: Array<{
    adId: string;
    archiveId: string;
    pageId: string;
    pageName: string;
    isActive: boolean;
    startDate?: string;
    endDate?: string;
    publisherPlatforms: string[];
    displayFormat: string;
    creativeType: string;
    imageUrls: string[];
    videoUrls: string[];
    cardCount: number;
    ctaType: string;
    ctaText: string;
    bodyText: string;
    titleText: string;
    captionText: string;
    linkUrl: string;
    sourceUrl: string;
  }>;
  metaInsights?: {
    adCount: number;
    creativeTypes: Array<{ type: string; count: number }>;
    ctaTypes: Array<{ ctaType: string; count: number }>;
    topPages: Array<{ pageName: string; count: number }>;
    publisherPlatforms: Array<{ platform: string; count: number }>;
  };
  metaRecommendations?: Array<{
    creativeType: "video" | "image" | "carousel" | "mixed" | "unknown";
    score: number;
    confidence: "high" | "medium" | "low";
    sampleSize: number;
    coveragePercent: number;
    activePercent: number;
    recencyPercent: number;
    suggestedCtaTypes: string[];
    rationale: string;
  }>;
  tiktokTopAds?: Array<{
    adId: string;
    videoUrl: string;
    coverImageUrl: string;
    likes: number;
    ctr: string;
    industry: string;
    objective: string;
    videoDuration: number;
    adCaption: string;
    region: string;
  }>;
  videoAnalyses?: Array<{
    adId: string;
    analysis: {
      visualStyle: string;
      hookStructure: string;
      pacing: string;
      productPlacement: string;
      ctaPlacement: string;
      textOverlays: Array<{ time: string; text: string }>;
      musicStyle: string;
      narrativeArc: string;
      keyTakeaways: string[];
    };
  }>;
  tiktokTrending?: {
    hashtags: Array<{ name: string; views: string; videoCount: number }>;
    trendingSearches: string[];
    trendingMusic: Array<{ track: string; usageCount: number }>;
  };
  contentBriefs?: Array<{
    productIndex: number;
    productTitle: string;
    tiktok?: ContentPlatformBrief;
    meta?: ContentPlatformBrief;
    google?: ContentPlatformBrief;
    crossPlatformSynergies: string;
    contentCalendar: Array<{ day: number; platform: string; action: string }>;
    creativeFatiguePlan: string;
  }>;
  productKeywords?: Array<{
    productIndex: number;
    productTitle: string;
    tiktokHashtags: string[];
    tiktokKeywords: string[];
    googleKeywords: string[];
    contentThemes: string[];
  }>;
  approvedBriefIds?: string[];
  approvedScriptKeys?: string[];
  editedScripts?: Record<string, { hook: string; body: string; cta: string; caption: string }>;
}

interface ContentPlatformBrief {
  contentType: string;
  hook: string;
  narrative: string;
  scriptVariants: Array<{
    variantName: string;
    hook: string;
    body: string;
    cta: string;
    caption: string;
    hashtags: string[];
  }>;
  visualDirection: string;
  musicSuggestion: string;
  formatSpecs: { dimensions: string; duration: string; fileFormat: string };
  estimatedCtrRange: string;
  abTestPlan: string;
}

export interface StepResult {
  success: boolean;
  data?: any;
  error?: string;
  creditsUsed?: number;
}

export type StepLog = { id: number; text: string; ts: string };

// ── Thought Stream ──────────────────────────────────────────────────────────

export type ThoughtEventKind =
  | "thinking" // Agent is reasoning/planning
  | "searching" // Fetching external data (scraping, API calls)
  | "analyzing" // Processing/distilling collected data
  | "creating" // Generating assets (logos, strategies, etc.)
  | "discovered" // Found something noteworthy
  | "completed" // Step/phase finished successfully
  | "waiting" // Needs user input or confirmation
  | "warning" // Non-fatal issue
  | "error"; // Something failed

export interface ThoughtEvent {
  id: number;
  ts: string;
  kind: ThoughtEventKind;
  /** Human-readable headline — the primary text users see */
  headline: string;
  /** Optional secondary detail line */
  detail?: string;
  /** Data source identifier for badge display (e.g. "twitter", "youtube") */
  source?: string;
  /** Progress indicator for ongoing operations */
  progress?: {
    current: number;
    total: number;
    unit?: string;
  };
  /** Original log text preserved for debugging */
  rawLog?: string;
}

export type CompanionInteractionMode = "chat" | "voice";

export interface WidgetHighlight {
  widgetId: string;
  comment: string;
  /** Auto-expire timestamp (epoch ms); 0 = persistent until dismissed */
  expiresAt: number;
  /** Optional short label for the action button (e.g. "Edit COGS", "Connect Meta") */
  actionLabel?: string;
  /** Pipeline step index this highlight belongs to */
  stepIndex?: number;
  /** Optional callback fired when the action button is clicked (not persisted) */
  onAction?: () => void;
}

export interface CoAgentState {
  // Session
  sessionId: string | null;
  chatId: string | null;
  isInitializing: boolean;
  error: string | null;

  // Copilot type discriminator
  coAgentType: CoAgentType;

  // Google Ads integration
  isGAdsConnected: boolean;
  gAdsStatus: string | null;
  gAdsCustomerId: string | null;
  showGAdsModal: boolean;

  // Meta / TikTok ad platform integration
  isMetaConnected: boolean;
  metaAccountName: string | null;
  isTikTokConnected: boolean;
  tikTokAccountName: string | null;

  hasStarted: boolean;
  selectedAgentId: string | null;
  agentStyle: string | null;
  campaignName: string;

  currentStepIndex: number;
  lastBriefedStep: number;
  focusedStepIndex: number | null;
  stepStatuses: Record<number, StepStatus>;
  stepLogs: Record<number, StepLog[]>;
  thoughtEvents: Record<number, ThoughtEvent[]>;

  messages: CoAgentMessage[];

  // Step loading
  isStepLoading: boolean;
  stepError: string | null;

  campaignData: CampaignData;

  initializeWorkspace: (
    agentId: string,
    agentStyle: string,
    name: string,
    coAgentType?: CoAgentType,
    workspaceId?: string,
  ) => Promise<string | null>;
  callStep: (step: number, userInput: Record<string, any>) => Promise<StepResult>;
  startResearch: (brandInput: BrandInput) => Promise<void>;
  startSocialResearch: (brandInput: SocialBrandInput, brandId?: string) => Promise<void>;
  startBrandResearch: (
    input: BrandCoAgentInput,
    options?: { mode?: "create" | "refresh"; targetBrandId?: string },
  ) => Promise<void>;
  confirmBrandStrategy: () => void;
  startGrowthResearch: (input: GrowthPipelineInput) => Promise<void>;
  continueGrowthResearch: () => Promise<void>;
  startGeoResearch: (input: GeoPipelineInput) => Promise<void>;
  startEcommerceResearch: (input: EcommercePipelineInput) => Promise<void>;
  startEcommerceMetrics: () => Promise<void>;
  startContentIntelligence: (input: {
    storeId: string;
    category: string;
    country: string;
    selectedProductIndex: number;
    products: Array<{
      index: number;
      title: string;
      category?: string;
      adAngle?: string;
      targetKeywords?: string[];
      price?: string;
      breakevenCpa?: number;
      estimatedCpa?: Record<string, number>;
    }>;
    brandProfile: Record<string, unknown>;
    adCostEstimates: unknown[];
  }) => Promise<void>;
  approveContentBrief: (briefId: string, action: "approve" | "reject") => Promise<void>;
  updateProductMetric: (productId: string, field: string, value: number) => void;
  bulkUpdateProductMetric: (productIds: string[], field: string, value: number) => void;
  advanceToStep: (stepIndex: number, status?: "running" | "waiting") => void;
  completeCurrentStep: () => void;
  setStepStatus: (stepIndex: number, status: StepStatus) => void;
  setStepWaiting: (stepIndex: number) => void;
  completeCurrentStepAndAdvance: (nextStatus?: "running" | "waiting") => void;
  setExecutionStep: (stepIndex: number, status?: "running" | "waiting") => void;
  advanceStep: () => void;
  addMessage: (msg: Omit<CoAgentMessage, "id" | "timestamp">) => void;
  addStepLog: (stepIndex: number, text: string) => void;
  addThoughtEvent: (stepIndex: number, event: Omit<ThoughtEvent, "id" | "ts">) => void;
  updateCampaignData: (data: Partial<CampaignData>) => void;
  startSocialVideoGeneration: (input: {
    chatId: string;
    source: "template" | "custom" | "brief";
    templateId?: string;
    briefId?: string;
    aspectRatio: "16:9" | "9:16";
    prompt?: string;
    overlayText?: string;
  }) => Promise<void>;
  generateSocialVideoPreview: (input: {
    chatId: string;
    templateId: string;
    aspectRatio?: "16:9" | "9:16";
    overlayText?: string;
    userPrompt?: string;
  }) => Promise<void>;
  sendBriefToProduction: (input: {
    chatId: string;
    briefId: string;
    pipeline: "cinematic" | "ugc";
  }) => Promise<void>;
  hydrateFromServer: (data: any) => void;
  /**
   * Mid-conversation refresh path — pulls only artifact-derived
   * campaignData (and assets/artifacts metadata) from the server,
   * leaving currentStepIndex/stepStatuses untouched.
   *
   * Use case: brand artifact lands during a streaming agent turn
   * (e.g. save_brand_research). We want the synthesis values to flow
   * into the wizard's render, but the agent's own `advance_step` tool
   * call (still in flight in the same stream) is the source of truth
   * for step progression. Letting full hydrateFromServer fire here
   * races advance_step and can mark the next step complete without it
   * actually running.
   */
  hydrateArtifactsOnly: (data: any) => void;
  reset: () => void;
  setFocusedStep: (index: number | null) => void;
  goBack: () => void;
  goNext: () => void;

  // Google Ads
  fetchGAdsStatus: () => Promise<void>;
  setShowGAdsModal: (show: boolean) => void;
  requireGAdsConnection: () => boolean;

  // Meta / TikTok
  fetchAdPlatformStatuses: () => Promise<void>;
  connectAdPlatform: (platform: "meta" | "tiktok") => Promise<void>;
  disconnectAdPlatform: (platform: "meta" | "tiktok") => Promise<void>;

  // Companion interaction mode & widget highlights
  companionMode: CompanionInteractionMode;
  setCompanionMode: (mode: CompanionInteractionMode) => void;
  copilotGreetingSent: boolean;
  setCopilotGreetingSent: (sent: boolean) => void;
  /** Session-scoped control-trigger registry to prevent duplicate auto control messages. */
  sentControlTriggerKeys: string[];
  hasControlTriggerBeenSent: (key: string) => boolean;
  markControlTriggerSent: (key: string) => void;
  clearControlTriggersForSession: (sessionId: string) => void;
  widgetHighlights: WidgetHighlight[];
  highlightWidget: (
    widgetId: string,
    comment: string,
    durationMs?: number,
    actionLabel?: string,
    stepIndex?: number,
    onAction?: () => void,
  ) => void;
  clearWidgetHighlight: (widgetId: string) => void;
  clearAllHighlights: () => void;

  // Widget context inspector (user→agent context attachment)
  attachedWidgetContexts: WidgetContext[];
  attachWidgetContext: (ctx: WidgetContext) => void;
  removeWidgetContext: (index: number) => void;
  clearWidgetContexts: () => void;

  // Tracks which widget contexts were sent with each chat message (messageId → contexts)
  sentMessageContexts: Record<string, WidgetContext[]>;
  recordSentContexts: (messageId: string, contexts: WidgetContext[]) => void;
}

// Re-export external types used by pipelines and store
export type {
  BrandInput,
  GeneratedQueryPlan,
  CollectionReport,
  GeoVisibilityMatrix,
  GeoPlan,
  AdsPlan,
  ContentPlan,
} from "@/config/coAgentPrompts";
export type {
  SocialBrandInput,
  SocialQueryPlan,
  TrendReport,
  CompetitorAudit,
  TemplateLibrary,
  SocialCalendar,
  VideoBriefBatch,
  EngagementBenchmark,
} from "@/config/socialCoAgentPrompts";
export type { BrandCoAgentInput } from "@/config/brandCoAgentPrompts";
export type {
  GrowthPipelineInput,
  GrowthTrackingEvent,
  GrowthCampaign,
} from "@/config/growthCoAgentPrompts";
export type {
  GeoPipelineInput,
  AiCitationResult,
  GeoScore,
  ContentGap,
  CopyUpgrade,
  JsonLdAsset,
  EntityPayload,
  SiteScanResult,
  GeoImplementationPack,
} from "@/config/geoCoAgentPrompts";
export type {
  EcommercePipelineInput,
  BrandProfile as EcommerceBrandProfile,
  PriorityScore,
} from "@/config/ecommerceCoAgentPrompts";
export type {
  BrandPopulateResult,
  BrandConfig,
  TwitterResearchResult,
  PerplexityResearchResult,
  YoutubeResearchResult,
  ResearchSynthesis,
} from "@/config/brand";
